import React, { useState, useEffect } from 'react';
import { Shield, Activity, Users, MessageSquare, TrendingUp, Download, BarChart3, UserCog, Calculator } from 'lucide-react';
import LoginActivity from './components/admin/LoginActivity';
import MarketplaceActivity from './components/admin/MarketplaceActivity';
import PostsActivity from './components/admin/PostsActivity';
import UsersActivity from './components/admin/UsersActivity';
import UsersAdmin from './UsersAdmin';
import AdminCapTable from './components/admin/AdminCapTable';
import AdminManagementAccount from './components/admin/AdminManagementAccount';
import { MetricCard } from './components/admin/MetricCard';
import { User } from './models/User';
import { getActivityStatistics } from './data/adminActivityDatabase.ts';
import { useAuth } from './hooks/useAuth';
import { getAllUsers, addUser, updateUser, removeUser } from './data/userDatabase.ts';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface AdminProps {
  user: User;
  onLogout: () => void;
  onNavigate?: (page: string) => void;
}

type AdminSubPage = 'users' | 'cap-table' | 'management-account' | 'activity';

function Admin({ user, onLogout, onNavigate }: AdminProps) {
  const [currentSubPage, setCurrentSubPage] = useState<AdminSubPage>('users');
  const [currentActivityPage, setCurrentActivityPage] = useState<'overview' | 'logins' | 'marketplace' | 'posts' | 'users'>('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState({
    totalActivities: 0,
    loginActivities: 0,
    marketplaceActivities: 0,
    postActivities: 0,
    userActivities: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load users for the UsersAdmin component
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        
        // Load activity stats
        const activityStats = await getActivityStatistics();
        setStats(activityStats);
      } catch (error) {
        console.error('Failed to load admin data:', error);
      }
    };
    
    loadData();
  }, [currentSubPage]);

  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleSubNavigation = (subPage: AdminSubPage) => {
    setCurrentSubPage(subPage);
  };

  const handleAddUser = async (newUser: User): Promise<User> => {
    try {
      // Use Cloud Function to create user
      const functions = getFunctions();
      const adminCreateUser = httpsCallable(functions, 'adminCreateUser');
      
      const result = await adminCreateUser({
        userData: {
          fullName: newUser.fullName,
          email: newUser.email,
          phoneNumber: newUser.phoneNumber,
          country: newUser.country,
          notes: newUser.notes,
          isAdmin: newUser.isAdmin || false
        },
        adminUserId: user.id
      });
      
      console.log('User creation result:', result.data);
      
      // Optimistic update: Add the new user to the existing list instead of reloading all users
      const resultData = result.data as { success: boolean; userData?: User };
      if (resultData.success && resultData.userData) {
        const createdUser = resultData.userData;
        setUsers(prevUsers => [...prevUsers, createdUser]);
        console.log('User added successfully via Cloud Function (optimistic update)');
        return createdUser;
      } else {
        // Fallback: reload users list if we don't have the user data
        console.log('Falling back to full users list reload');
        const updatedUsers = await getAllUsers();
        setUsers(updatedUsers);
        // Find the newly created user in the updated list
        const createdUser = updatedUsers.find(u => 
          u.fullName === newUser.fullName && 
          u.email === newUser.email
        );
        if (createdUser) {
          return createdUser;
        } else {
          throw new Error('User was created but could not be found in the updated list');
        }
      }
      
    } catch (error) {
      console.error('Failed to add user:', error);
      throw error;
    }
  };

  const handleUpdateUser = async (userToUpdate: User) => {
    try {
      await updateUser(userToUpdate);
      
      // Reload users list
      const updatedUsers = await getAllUsers();
      setUsers(updatedUsers);
      
      console.log('User updated successfully:', userToUpdate);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      // Use Cloud Function to delete user from both Firestore and Firebase Auth
      const functions = getFunctions();
      const adminDeleteUser = httpsCallable(functions, 'adminDeleteUser');
      
      const result = await adminDeleteUser({
        userId: userId,
        adminUserId: user.id
      });
      
      console.log('User deletion result:', result.data);
      
      // Reload users list
      const updatedUsers = await getAllUsers();
      setUsers(updatedUsers);
      
      console.log('User removed successfully via Cloud Function:', userId);
    } catch (error) {
      console.error('Failed to remove user:', error);
      throw error;
    }
  };

  const renderActivitySubPage = () => {
    switch (currentActivityPage) {
      case 'logins':
        return <LoginActivity user={user} />;
      case 'marketplace':
        return <MarketplaceActivity user={user} />;
      case 'posts':
        return <PostsActivity user={user} />;
      case 'users':
        return <UsersActivity user={user} />;
      default:
        return renderOverview();
    }
  };

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Monitor system health, user activity, and application performance
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <MetricCard
          title="Login Activities"
          value={stats.loginActivities}
          trend={{
            value: 12,
            label: "vs. last week",
            direction: "up"
          }}
          icon={Shield}
          color="green"
          actionButton="View Details"
          onClick={() => setCurrentActivityPage('logins')}
        />

        <MetricCard
          title="Marketplace Activity"
          value={stats.marketplaceActivities}
          trend={{
            value: 3,
            label: "vs. last week",
            direction: "down"
          }}
          icon={TrendingUp}
          color="purple"
          actionButton="View Activity"
          onClick={() => setCurrentActivityPage('marketplace')}
        />

        <MetricCard
          title="Timeline Posts"
          value={stats.postActivities}
          trend={{
            value: 15,
            label: "vs. last week",
            direction: "up"
          }}
          icon={MessageSquare}
          color="indigo"
          actionButton="Review Posts"
          onClick={() => setCurrentActivityPage('posts')}
        />

        <MetricCard
          title="User Activities"
          value={stats.userActivities}
          trend={{
            value: 8,
            label: "vs. last week",
            direction: "up"
          }}
          icon={Users}
          color="blue"
          actionButton="View Activities"
          onClick={() => setCurrentActivityPage('users')}
        />
      </div>

      {/* Quick Actions Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => setCurrentSubPage('users')}
            className="p-4 text-left rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group"
          >
            <UserCog className="w-8 h-8 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-medium text-gray-900">Manage Users</div>
            <div className="text-sm text-gray-500">Add and edit users</div>
          </button>

          <button
            onClick={() => setCurrentSubPage('cap-table')}
            className="p-4 text-left rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-200 group"
          >
            <BarChart3 className="w-8 h-8 text-green-500 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-medium text-gray-900">Cap Table</div>
            <div className="text-sm text-gray-500">View shareholdings</div>
          </button>

          <button
            onClick={() => setCurrentSubPage('management-account')}
            className="p-4 text-left rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group"
          >
            <Calculator className="w-8 h-8 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-medium text-gray-900">Management Account</div>
            <div className="text-sm text-gray-500">Financial reports</div>
          </button>

          <button
            onClick={() => setCurrentActivityPage('logins')}
            className="p-4 text-left rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 group"
          >
            <Activity className="w-8 h-8 text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
            <div className="font-medium text-gray-900">Activity Logs</div>
            <div className="text-sm text-gray-500">Monitor all activities</div>
          </button>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">System Information</h2>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Total Activities Tracked</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">{stats.totalActivities.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Current User</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <span className="font-medium">{user.fullName}</span>
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Super Admin
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Activity</dt>
            <dd className="mt-1 text-sm text-gray-900">Just now</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">System Status</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></span>
                Online
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );

  const renderActivityPage = () => {
    return (
      <div>
        {/* Activity Sub-navigation */}
        <div className="mb-6">
          <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto scrollbar-hide pb-2">
            <button
              onClick={() => setCurrentActivityPage('overview')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                currentActivityPage === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setCurrentActivityPage('logins')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                currentActivityPage === 'logins'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Login Activity</span>
              <span className="sm:hidden">Logins</span>
            </button>
            <button
              onClick={() => setCurrentActivityPage('marketplace')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                currentActivityPage === 'marketplace'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Marketplace Activity</span>
              <span className="sm:hidden">Market</span>
            </button>
            <button
              onClick={() => setCurrentActivityPage('posts')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                currentActivityPage === 'posts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Posts Activity</span>
              <span className="sm:hidden">Posts</span>
            </button>
            <button
              onClick={() => setCurrentActivityPage('users')}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                currentActivityPage === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="hidden sm:inline">User Activities</span>
              <span className="sm:hidden">Users</span>
            </button>
          </nav>
        </div>
        
        {renderActivitySubPage()}
      </div>
    );
  };

  const renderSubPage = () => {
    switch (currentSubPage) {
      case 'users':
        return (
          <UsersAdmin 
            currentUser={user}
            users={users}
            onAddUser={handleAddUser}
            onUpdateUser={handleUpdateUser}
            onRemoveUser={handleRemoveUser}
            onLogout={onLogout}
          />
        );
      case 'cap-table':
        return <AdminCapTable user={user} />;
      case 'management-account':
        return <AdminManagementAccount user={user} />;
      case 'activity':
        return renderActivityPage();
      default:
        return renderActivityPage();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Sub-navigation */}
        <div className="mb-6">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide border-b border-gray-200 pb-0">
            <button
              onClick={() => handleSubNavigation('users')}
              className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                currentSubPage === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserCog className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Users</span>
              <span className="sm:hidden">Users</span>
            </button>
            <button
              onClick={() => handleSubNavigation('cap-table')}
              className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                currentSubPage === 'cap-table'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Cap Table</span>
              <span className="sm:hidden">Cap</span>
            </button>
            <button
              onClick={() => handleSubNavigation('management-account')}
              className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                currentSubPage === 'management-account'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calculator className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Management Accounts</span>
              <span className="sm:hidden">Mgmt</span>
            </button>
            <button
              onClick={() => handleSubNavigation('activity')}
              className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                currentSubPage === 'activity'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Activity</span>
              <span className="sm:hidden">Activity</span>
            </button>
          </nav>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentSubPage === 'activity' ? 'Activity Dashboard' : 
               currentSubPage === 'users' ? 'User Management' :
               currentSubPage === 'cap-table' ? 'Cap Table Management' : 'Management Accounts'}
            </h2>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Super Admin Access
          </span>
        </div>
        
        {renderSubPage()}
      </main>
    </div>
  );
}

export default Admin; 